import { useState } from "react";
import { invokeAdmin, errMsg } from "../lib/adminApi";
import { useAdminQuery } from "../hooks/useAdminQuery";
import { Banner, CopyId, PillTabs } from "../components/ui";

type ClaimStatus = "pending" | "approved" | "rejected";

type ClaimRequest = {
  id: string;
  user_id: string;
  entity_kind: "venue" | "organizer";
  entity_id: string;
  evidence: string | null;
  status: ClaimStatus;
  admin_notes: string | null;
  reviewed_at: string | null;
  created_at: string;
};

const TABS: { key: ClaimStatus; label: string }[] = [
  { key: "pending", label: "Pending" },
  { key: "approved", label: "Approved" },
  { key: "rejected", label: "Rejected" },
];

export default function EntityClaimApprovalsPage() {
  const [tab, setTab] = useState<ClaimStatus>("pending");
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  // Cancellation-safe fetch keyed on the tab (ADM-5).
  const { data, loading, error, refetch } = useAdminQuery(
    () =>
      invokeAdmin<{ claims: ClaimRequest[]; total?: number }>(
        "admin_entity_claims_list",
        { status: tab, limit: 50 },
      ),
    [tab],
  );
  const claims = data?.claims ?? [];

  async function review(claim: ClaimRequest, status: "approved" | "rejected") {
    // Approve/Reject only render on the Pending tab, so a mis-click is otherwise
    // unrecoverable in the UI — confirm first (ADM-4).
    const verb = status === "approved" ? "Approve" : "Reject";
    if (
      !window.confirm(
        `${verb} the ${claim.entity_kind} claim ${claim.entity_id} by user ${claim.user_id}?`,
      )
    )
      return;
    setActionLoading(claim.id);
    setActionError(null);
    try {
      await invokeAdmin("admin_entity_claim_update", {
        id: claim.id,
        status,
        admin_notes: notes[claim.id] ?? undefined,
      });
      refetch();
    } catch (e) {
      setActionError(errMsg(e));
    } finally {
      setActionLoading(null);
    }
  }

  return (
    <div>
      <h1 className="text-xl font-semibold">Claims</h1>

      <PillTabs className="mt-4" tabs={TABS} active={tab} onChange={setTab} />

      {error && <Banner className="mt-3">{error}</Banner>}
      {actionError && <Banner className="mt-3">{actionError}</Banner>}

      <div className="mt-4 rounded-lg border border-zinc-800 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-950 text-left text-xs uppercase tracking-wide text-zinc-500">
            <tr>
              <th className="px-3 py-2 font-medium">Kind</th>
              <th className="px-3 py-2 font-medium">Entity</th>
              <th className="px-3 py-2 font-medium">User</th>
              <th className="px-3 py-2 font-medium">Evidence</th>
              <th className="px-3 py-2 font-medium">Created</th>
              {tab === "pending" && <th className="px-3 py-2 font-medium"></th>}
            </tr>
          </thead>
          <tbody>
            {claims.map((c) => (
              <tr key={c.id} className="border-t border-zinc-800 align-top">
                <td className="px-3 py-2 text-zinc-300">{c.entity_kind}</td>
                <td className="px-3 py-2"><CopyId id={c.entity_id} /></td>
                <td className="px-3 py-2"><CopyId id={c.user_id} /></td>
                <td className="px-3 py-2 text-zinc-400 text-xs max-w-xs">
                  {c.evidence ? (
                    <span className="whitespace-pre-wrap break-words">{c.evidence}</span>
                  ) : (
                    "—"
                  )}
                </td>
                <td className="px-3 py-2 text-zinc-400 text-xs">
                  {c.created_at ? new Date(c.created_at).toLocaleDateString() : "—"}
                </td>
                {tab === "pending" && (
                  <td className="px-3 py-2">
                    <div className="flex flex-col gap-2">
                      <input
                        type="text"
                        value={notes[c.id] ?? ""}
                        onChange={(e) =>
                          setNotes((prev) => ({ ...prev, [c.id]: e.target.value }))
                        }
                        placeholder="Admin notes…"
                        className="w-48 rounded border border-zinc-700 bg-zinc-800 px-2 py-1 text-xs text-white placeholder-zinc-500 focus:outline-none focus:ring-1 focus:ring-zinc-500"
                      />
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => review(c, "approved")}
                          disabled={actionLoading === c.id}
                          className="rounded bg-green-800 px-2 py-0.5 text-xs text-white hover:bg-green-700 disabled:opacity-50"
                        >
                          {actionLoading === c.id ? "…" : "Approve"}
                        </button>
                        <button
                          onClick={() => review(c, "rejected")}
                          disabled={actionLoading === c.id}
                          className="rounded bg-red-800 px-2 py-0.5 text-xs text-white hover:bg-red-700 disabled:opacity-50"
                        >
                          {actionLoading === c.id ? "…" : "Reject"}
                        </button>
                      </div>
                    </div>
                  </td>
                )}
              </tr>
            ))}
            {claims.length === 0 && !loading && (
              <tr>
                <td
                  colSpan={tab === "pending" ? 6 : 5}
                  className="px-3 py-4 text-center text-sm text-zinc-500"
                >
                  No {tab} claims.
                </td>
              </tr>
            )}
            {loading && (
              <tr>
                <td
                  colSpan={tab === "pending" ? 6 : 5}
                  className="px-3 py-4 text-center text-sm text-zinc-500"
                >
                  Loading…
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
